import React, { useEffect, useState } from "react";
import { DateRangePicker } from "react-date-range";
import { toast, Toaster } from "react-hot-toast";
import Modal from 'react-modal';
import Loading from "../components/Loading";
import TableRenderer from "../components/TableRenderer";
import TotalsSummary from "../components/TotalsSummary.jsx";
import { useGlobalContext } from "../context/globalContext";
import axios from "axios";

function Dashboard() {
    const { userInfo } = useGlobalContext();
    const [data, setData] = useState([]);
    const [columns, setColumns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [agents, setAgents] = useState([]);
    const API_BASE_URL = "https://66z6lhu4ge.execute-api.ap-south-1.amazonaws.com/prod/call";

// Default date range to current date
    const today = new Date();
    const [selectionRange, setSelectionRange] = useState({
        startDate: today,
        endDate: today,
        key: 'selection',
    });
    const [selectedAgent, setSelectedAgent] = useState('');
    const [showCalendar, setShowCalendar] = useState(false);
    const [totals, setTotals] = useState({});

    const calculateTotals = (data) => {
        const uniqueAgents = Array.from(new Set(data.map(row => row.agent_number)));
        const agentCount = uniqueAgents.length;

        const totals = data.reduce((totals, row) => {
            totals.total_calls += row.total_calls || 0;
            totals.duration += row.duration || 0;
            totals.oc_answered += row.oc_answered || 0;
            totals.oc_missed += row.oc_missed || 0;
            totals.ic_answered += row.ic_answered || 0;
            totals.ic_missed += row.ic_missed || 0;
            totals.agent_count = agentCount;
            totals.total_rows += 1;
            return totals;
        }, {
            total_calls: 0,
            duration: 0,
            oc_answered: 0,
            oc_missed: 0,
            ic_answered: 0,
            ic_missed: 0,
            agent_count: 0,
            total_rows: 0
        });

        totals.avg_calls = totals.total_rows ? (totals.total_calls / totals.total_rows) : 0;
        totals.avg_duration = totals.total_rows ? (totals.duration / totals.total_rows) : 0;

        return totals;
    };

    const getFilteredColumns = (data) => {
        const columnOrder = [
            "date",
            "agent_number",
            "agent_name",
            "total_calls",
            "duration",
            "oc_answered",
            "oc_missed",
            "ic_answered",
            "ic_missed",
        ];

        const uniqueColumns = Array.from(
            new Set(data.flatMap((response) => Object.keys(response)))
        ).map((key) => ({
            id: key,
            header: key,
            accessorKey: key,
            footer: key,
        }));

        return uniqueColumns.sort(
            (a, b) => columnOrder.indexOf(a.id) - columnOrder.indexOf(b.id)
        );
    };

    const toggleCalendar = () => {
        setShowCalendar(!showCalendar);
    };

    function handleSelectDateRange(date) {
        const startDate = new Date(date.selection.startDate);
        const endDate = new Date(date.selection.endDate);

        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);

        setSelectionRange({
            startDate,
            endDate,
            key: 'selection',
        });

        getData(selectedAgent, startDate, endDate);
        toggleCalendar();
    }

    const fetchAgents = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/get-agent-data`);
            setAgents(response.data);
        } catch (error) {
            toast.error("Error fetching agents");
            console.error("Error fetching agents:", error);
        }
    };

    function handleSelectAgent(agentNumber) {
        setSelectedAgent(agentNumber);
        getData(agentNumber, selectionRange.startDate, selectionRange.endDate);
    }

    const formatDuration = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = seconds % 60;
        return `${seconds} (${hours.toString().padStart(2, '0')}h ${minutes.toString().padStart(2, '0')}m ${remainingSeconds.toString().padStart(2, '0')}s)`;
    };

    const getData = async (selectedAgent, startDate, endDate) => {
        setLoading(true);
        setData(null);
        try {
            const formattedStartDate = startDate.toISOString().split('T')[0];
            const formattedEndDate = endDate.toISOString().split('T')[0];

            // If no specific agent is selected or "All" is selected
            if (!selectedAgent || selectedAgent === "All") {
                // First get all agent numbers
                const agentResponse = await axios.get(`${API_BASE_URL}/get-agent-data`);
                const agentList = agentResponse.data;

                // Then fetch data for each agent
                const allData = [];
                for (const agent of agentList) {
                    const params = new URLSearchParams({
                        start_date: formattedStartDate,
                        end_date: formattedEndDate,
                        agent_number: agent.agent_number
                    });

                    const response = await axios.get(`${API_BASE_URL}/get-data?${params}`);
                    if (response.data && response.data.length > 0) {
                        // Add agent name to each record
                        const enrichedData = response.data.map(record => ({
                            ...record,
                            agent_name: agent.name,
                            duration: formatDuration(record.duration)
                        }));
                        allData.push(...enrichedData);
                    }
                }

                if (allData.length === 0) {
                    toast.error("No data found for the selected date range.");
                    setData([]);
                    setColumns([]);
                    setTotals({});
                    return;
                }

                const sortedData = allData.sort((a, b) => new Date(a.date) - new Date(b.date));
                setData(sortedData);
                setColumns(getFilteredColumns(sortedData));
                const calculatedTotals = calculateTotals(sortedData);
                setTotals(calculatedTotals);
                toast.success("Data loaded successfully");
            } else {
                // Fetch data for a specific agent
                const params = new URLSearchParams({
                    start_date: formattedStartDate,
                    end_date: formattedEndDate,
                    agent_number: selectedAgent
                });

                const response = await axios.get(`${API_BASE_URL}/get-data?${params}`);
                
                // Enrich data with agent name
                const agentName = agents.find(a => a.agent_number === selectedAgent)?.name;
                const enrichedData = response.data.map(record => ({
                    ...record,
                    agent_name: agentName,
                    duration: formatDuration(record.duration)
                }));

                if (enrichedData.length === 0) {
                    toast.error("No data found for the selected agent and date range.");
                    setData([]);
                    setColumns([]);
                    setTotals({});
                    return;
                }

                const sortedData = enrichedData.sort((a, b) => new Date(a.date) - new Date(b.date));
                setData(sortedData);
                setColumns(getFilteredColumns(sortedData));
                const calculatedTotals = calculateTotals(sortedData);
                setTotals(calculatedTotals);
                toast.success("Data loaded successfully");
            }
        } catch (err) {
            toast.error(err.message);
            console.error("Error:", err);
            setData([]);
            setColumns([]);
            setTotals({});
        } finally {
            setLoading(false);
        }
    };

    const customStyles = {
        content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: "#505050",
            border: "none",
            borderRadius: "10px",
            padding: "0",
        },
    };

    useEffect(() => {
        if (userInfo?.email) {

            fetchAgents();
            getData(selectedAgent, selectionRange.startDate, selectionRange.endDate);
        }
    }, [userInfo?.email]);

    return (
        <div className="p-2 w-full">
            <Toaster
                position="bottom-left"
                toastOptions={{
                    className: "",
                    style: {
                        background: "#ff8e00",
                        color: "#2e2c2d",
                    },
                    success: {
                        duration: 2000,
                        theme: {
                            primary: "green",
                            secondary: "black",
                        },
                    },
                }}
            />
            <Modal
                style={customStyles}
                appElement={document.getElementById('root')}
                isOpen={showCalendar}
                onRequestClose={toggleCalendar}
            >
                <DateRangePicker
                    ranges={[selectionRange]}
                    onChange={handleSelectDateRange}
                    showSelectionPreview={true}
                    moveRangeOnFirstSelection={false}
                    direction="horizontal"
                />
            </Modal>
            <div>
                <h1 className="text-2xl font-bold w-full text-center">Dashboard</h1>

                <button
                    className="bg-theme-yellow-light text-black rounded px-3 py-2 hover:bg-theme-yellow-dark"
                    onClick={toggleCalendar}
                >
                    Show Calendar
                </button>

                <select
                    className="mx-3 px-3 py-2 rounded border border-theme-yellow-light cursor-pointer"
                    onChange={(e) => handleSelectAgent(e.target.value)}
                    value={selectedAgent}
                >
                    <option value="All">All Agents</option>
                    {agents.map((agent) => (
                        <option key={agent.agent_number} value={agent.agent_number}>
                            {agent.name}
                        </option>
                    ))}
                </select>
                <div className="p-2">
                    <div>
                        Date Range:
                        <span className="text-lg font-bold ml-2">
                            {selectionRange.startDate.toDateString()} - {selectionRange.endDate.toDateString()}
                        </span>
                    </div>
                    <div>
                        Agent:
                        <span className="text-lg font-bold ml-2">
                            {agents.find(a => a.agent_number === selectedAgent)?.name || 'All Agents'}
                        </span>
                    </div>
                </div>
            </div>

            {loading && !data ? (
                <Loading />
            ) : data?.length === 0 ? (
                <p className="text-xl mt-5 text-center w-full">
                    No data found for the selected criteria.
                </p>
            ) : (
                data && <>
                    <TableRenderer data={data} columns={columns} />
                    <TotalsSummary totals={totals} />
                </>
            )}
        </div>
    );
}

export default Dashboard;